// Voice signatures, the person's and the company's (BUILD 2 C2-6 as amended by
// addendum-2): the response shapes of /api/v1/company/voice/*. The subject is
// always the session user, and no response carries a vector or a score. Every
// non-GET sends a JSON body, `{}` for the DELETE routes. The server's zod
// schemas are src/company-voice/routes.mjs; test/voice-wire-parity.mjs pins
// both halves.
//
// WP2-0b wrote the types; WP2-4 owns this file and adds the calls against
// WP2-3's routes, through companyRequest, whose CompanyRequestError carries a
// refusal's `reason` for ui/voice/error-copy.ts.
import type { EuAppLanguage } from '@/i18n/eu-locales';
import { CompanyRequestError, companyRequest } from './api';

/** GET voice/me: the person's own consent and voiceprint. */
export type VoiceMe = {
  available: boolean;
  reason?: 'disabled' | 'key_missing' | 'models_missing' | 'runtime_missing' | 'model_integrity';
  org: { signaturesEnabled: boolean };
  consent: VoiceConsent | null;
  currentConsentVersion: string;
  voiceprint: Voiceprint | null;
  promptDismissed: boolean;
};

/** POST voice/me/consent `{version, method, locale}` answers `{consent}`. */
export type VoiceConsent = { version: string; grantedAt: string };
/**
 * `locale` is the catalog language the consent text was shown in
 * (useAppCatalogLocale), stored on the consent row for the audit trail.
 */
export type VoiceConsentInput = {
  version: string;
  method: 'settings' | 'prompt';
  locale: EuAppLanguage;
};

/** `stale_model`: the voice model changed, and the person sets up a signature again. */
export type Voiceprint = {
  status: 'active' | 'stale_model';
  thresholdClass: 'recording' | 'meeting';
  sources: VoiceprintSource[];
  createdAt: string;
  lastSuggestedAt: string | null;
  lastConfirmedAt: string | null;
  suggestionCount?: number;
};
export type VoiceprintSource = {
  id: string;
  kind: 'recording' | 'meeting';
  meetingTitle?: string;
  date: string;
  speechS: number;
};

/** POST voice/me/enrolments: 201 for a recording, 202 for the person's own meeting speech. */
export type EnrolmentStartInput =
  | { kind: 'recording' }
  | { kind: 'meeting'; meetingId: string; label: string };
export type RecordingEnrolment = {
  enrolmentId: string;
  sampleRate: 16000;
  frameMs: 100;
  maxChunks: number;
};
export type MeetingEnrolment = { enrolmentId: string };
/** GET voice/me/enrolments/:id (410 voice_enrolment_expired after a restart). */
export type EnrolmentStatus = {
  status: 'receiving' | 'processing' | 'done' | 'failed';
  failureCode?: string;
};

/** GET / PUT voice/settings (owner only). Owners see the enrolled count, never who. */
export type CompanyVoiceSettings = {
  voicePassEnabled: boolean;
  signaturesEnabled: boolean;
  unusedRetentionDays: number;
  enabledAt: string | null;
};
export type CompanyVoiceSettingsResponse = {
  settings: CompanyVoiceSettings;
  enrolledCount: number;
};
/** Switching signatures off purges every voiceprint, so it needs `purge: true`. */
export type CompanyVoiceSettingsInput = Partial<Omit<CompanyVoiceSettings, 'enabledAt'>> & {
  purge?: boolean;
};

/**
 * GET voice/audit (owner) and GET voice/me/audit (the person's own). The
 * owner's person-level events carry no subject, so no per-person enrolment
 * status can be rebuilt from them.
 */
export type VoiceAuditEvent = {
  id: string;
  action: string;
  at: string;
  counts?: Record<string, number>;
};
export type VoiceAuditPage = { events: VoiceAuditEvent[]; next: string | null };

export const voiceApi = {
  me: () => companyRequest<VoiceMe>('/api/v1/company/voice/me'),
  consent: (input: VoiceConsentInput) =>
    companyRequest<{ consent: VoiceConsent }>('/api/v1/company/voice/me/consent', 'POST', input),
  delete: () =>
    companyRequest<{ consent: null; voiceprint: null }>('/api/v1/company/voice/me', 'DELETE', {}),
  dismissPrompt: () =>
    companyRequest<void>('/api/v1/company/voice/me/prompt-dismissed', 'POST', {}),
  startEnrolment: (input: EnrolmentStartInput) =>
    companyRequest<RecordingEnrolment | MeetingEnrolment>(
      '/api/v1/company/voice/me/enrolments',
      'POST',
      input,
    ),
  enrolFromMeeting: (meetingId: string, label: string) =>
    companyRequest<MeetingEnrolment>('/api/v1/company/voice/me/enrolments', 'POST', {
      kind: 'meeting',
      meetingId,
      label,
    }),
  enrolmentStatus: (id: string) =>
    companyRequest<EnrolmentStatus>(
      `/api/v1/company/voice/me/enrolments/${encodeURIComponent(id)}`,
    ),
  finishEnrolment: (id: string) =>
    companyRequest<EnrolmentStatus>(
      `/api/v1/company/voice/me/enrolments/${encodeURIComponent(id)}/finish`,
      'POST',
      {},
    ),
  removeSource: (id: string) =>
    companyRequest<{ voiceprint: Voiceprint | null }>(
      `/api/v1/company/voice/me/sources/${encodeURIComponent(id)}`,
      'DELETE',
      {},
    ),
  settings: () => companyRequest<CompanyVoiceSettingsResponse>('/api/v1/company/voice/settings'),
  putSettings: (input: CompanyVoiceSettingsInput) =>
    companyRequest<CompanyVoiceSettingsResponse>('/api/v1/company/voice/settings', 'PUT', input),
  audit: (next?: string | null) =>
    companyRequest<VoiceAuditPage>(
      `/api/v1/company/voice/audit${next ? `?next=${encodeURIComponent(next)}` : ''}`,
    ),
  uploadChunk: async (id: string, chunk: number, bytes: Uint8Array, sha256: string) => {
    const response = await fetch(
      `/api/v1/company/voice/me/enrolments/${encodeURIComponent(id)}/chunks/${chunk}`,
      {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/octet-stream', 'x-poneva-content-sha256': sha256 },
        body: bytes as BodyInit,
      },
    );
    if (!response.ok) throw new CompanyRequestError(response.status, 'enrolment_chunk_failed');
  },
};
